/**
 * VYORO Sales Partner — storefront script.
 *
 * Two independent jobs, both deliberately small:
 *
 *  1. Referral capture. Reads ?vp=CODE from a partner's link, remembers it,
 *     and writes it onto the Shopify cart as a cart attribute. Shopify then
 *     carries that attribute through checkout onto the order, where the
 *     backend reads it. Checkout itself is never touched.
 *
 *  2. The job application form. Posts to the partner service and renders the
 *     result. It records an application; it cannot create a partner.
 *
 * Nothing here is trusted by the backend. The referral code is validated
 * server-side against active partners, and the application is validated again
 * before anything is stored. This script is a convenience, not a gatekeeper.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'vyoro_partner_ref';
  var ATTRIBUTE_KEY = 'vyoro_partner';
  var CODE_PATTERN = /^[A-Z0-9]{4,24}$/;

  /* ---------------------------------------------------------------- *
   * Small helpers
   * ---------------------------------------------------------------- */

  function normaliseCode(value) {
    if (typeof value !== 'string') return null;
    var code = value.trim().toUpperCase();
    return CODE_PATTERN.test(code) ? code : null;
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.code !== 'string') return null;
      // Expire the attribution locally so a very old link does not follow a
      // shopper forever. The server has its own window as the real authority.
      if (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return normaliseCode(parsed.code);
    } catch (e) {
      // Private mode, disabled storage, corrupt value — all non-fatal.
      return null;
    }
  }

  function store(code, days) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ code: code, expiresAt: Date.now() + days * 86400000 })
      );
    } catch (e) {
      /* storage unavailable; the cart attribute below still does the work */
    }
  }

  /* ---------------------------------------------------------------- *
   * 1. Referral capture
   * ---------------------------------------------------------------- */

  function captureReferral() {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = normaliseCode(params.get('vp') || params.get('vyoro_ref'));

    // Last touch wins: a fresh link replaces whatever was remembered before.
    if (fromUrl) store(fromUrl, 30);

    var code = fromUrl || readStored();
    if (!code) return;

    writeCartAttribute(code);
  }

  /**
   * Attach the code to the cart.
   *
   * Reads the cart first so an existing attribute is not rewritten on every
   * page view, and so we never POST for anonymous browsers that will not check
   * out. Failures are swallowed: a shopper must never see an error because of
   * attribution.
   */
  function writeCartAttribute(code) {
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        var current = cart.attributes && cart.attributes[ATTRIBUTE_KEY];
        if (current === code) return;

        var body = {};
        body[ATTRIBUTE_KEY] = code;

        return fetch(
          (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/update.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ attributes: body })
          }
        );
      })
      .catch(function () {
        /* attribution is best-effort on the client; the server decides */
      });
  }

  /* ---------------------------------------------------------------- *
   * 2. Application form
   * ---------------------------------------------------------------- */

  var FIELDS = ['fullName', 'age', 'city', 'phone', 'email', 'whyVyoro', 'howGenerateSales'];

  function clearErrors(form) {
    FIELDS.forEach(function (name) {
      var el = form.querySelector('[data-error-for="' + name + '"]');
      if (el) {
        el.textContent = '';
        el.hidden = true;
      }
      var input = form.elements[name];
      if (input && input.closest) {
        var field = input.closest('.field');
        if (field) field.classList.remove('field--error');
      }
    });
  }

  function showErrors(form, errors) {
    var first = null;
    errors.forEach(function (problem) {
      var el = form.querySelector('[data-error-for="' + problem.field + '"]');
      if (el) {
        el.textContent = problem.message;
        el.hidden = false;
      }
      var input = form.elements[problem.field];
      if (input) {
        if (!first) first = input;
        var field = input.closest && input.closest('.field');
        if (field) field.classList.add('field--error');
      }
    });
    if (first && first.focus) first.focus();
  }

  function setMessage(box, kind, title, text) {
    if (!box) return;
    var accent =
      kind === 'ok' ? 'var(--color-success)' : kind === 'warn' ? 'var(--color-warning)' : 'var(--color-error)';
    box.innerHTML = '';

    var wrap = document.createElement('div');
    wrap.style.border = '1px solid var(--color-border-strong)';
    wrap.style.borderLeft = '3px solid ' + accent;
    wrap.style.borderRadius = 'var(--radius-btn)';
    wrap.style.padding = 'var(--space-4)';

    var h = document.createElement('strong');
    h.style.display = 'block';
    h.style.marginBottom = '4px';
    h.textContent = title;

    var p = document.createElement('p');
    p.style.margin = '0';
    p.style.color = 'var(--color-text-muted)';
    // textContent, never innerHTML: the message may echo server text.
    p.textContent = text;

    wrap.appendChild(h);
    wrap.appendChild(p);
    box.appendChild(wrap);
    box.hidden = false;
  }

  function initForm() {
    var form = document.getElementById('vyoro-apply-form');
    if (!form) return;

    var service = (form.getAttribute('data-service') || '').replace(/\/+$/, '');
    if (!service) return;

    var button = document.getElementById('vyoro-apply-submit');
    var message = document.getElementById('vyoro-apply-message');
    var submitting = false;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting) return;
      submitting = true;

      clearErrors(form);
      if (message) message.hidden = true;
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      var payload = {};
      FIELDS.forEach(function (name) {
        var input = form.elements[name];
        payload[name] = input ? input.value : '';
      });
      var honeypot = form.elements['company_website'];
      if (honeypot) payload.company_website = honeypot.value;

      fetch(service + '/public/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { status: res.status, data: data };
          });
        })
        .then(function (result) {
          var data = result.data || {};

          if (result.status === 201 || (result.status === 200 && data.ok)) {
            // Replace the form entirely. There is nothing more to submit, and
            // leaving it in place invites duplicate applications.
            var done = document.createElement('div');
            done.style.border = '1px solid var(--color-border-strong)';
            done.style.borderLeft = '3px solid var(--color-success)';
            done.style.borderRadius = 'var(--radius-btn)';
            done.style.padding = 'var(--space-5)';

            var heading = document.createElement('h3');
            heading.style.marginTop = '0';
            heading.textContent = 'Application Received';

            var body = document.createElement('p');
            body.style.color = 'var(--color-text-muted)';
            body.textContent =
              data.message ||
              'Thank you for applying to the VYORO Part-Time Sales Job. Our team will ' +
                'review your application. Only selected candidates will receive Sales Partner access.';

            done.appendChild(heading);
            done.appendChild(body);

            if (data.reference) {
              var ref = document.createElement('p');
              ref.style.marginBottom = '0';
              ref.style.fontSize = '0.875rem';
              ref.style.color = 'var(--color-text-muted)';
              ref.textContent = 'Your reference: ' + data.reference;
              done.appendChild(ref);
            }

            form.parentNode.replaceChild(done, form);
            done.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          if (result.status === 422 && Array.isArray(data.errors)) {
            showErrors(form, data.errors);
            setMessage(message, 'error', 'Please check the highlighted fields', data.message || '');
          } else if (result.status === 409) {
            setMessage(message, 'warn', 'We already have your application', data.message || '');
          } else if (result.status === 503) {
            setMessage(message, 'warn', 'Applications are closed', data.message || '');
          } else if (result.status === 429) {
            setMessage(message, 'warn', 'Too many attempts', data.error || data.message || '');
          } else {
            setMessage(
              message,
              'error',
              'Could not send your application',
              data.message || data.error || 'Please try again in a moment, or apply by email instead.'
            );
          }
        })
        .catch(function () {
          setMessage(
            message,
            'error',
            'Could not reach VYORO',
            'Check your connection and try again, or apply by email instead.'
          );
        })
        .finally(function () {
          submitting = false;
          if (button) {
            button.disabled = false;
            button.textContent = 'Apply for Part-Time Sales Job';
          }
        });
    });
  }

  /* ---------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------- */

  function boot() {
    try {
      captureReferral();
    } catch (e) {
      /* never let attribution break the storefront */
    }
    try {
      initForm();
    } catch (e) {
      /* nor the page */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
